import {
	armsShown,
	canToggle,
	parseOpenColumns,
	REVIEW_COLUMN_ORDER,
	type ReviewColumn,
	serialiseOpenColumns,
} from "./columns";

export type ColumnDeclaration = {
	readonly column: ReviewColumn;
	readonly label: string;
	readonly openByDefault: boolean;
};

/**
 * Which of a screen's columns are open. The screen declares its columns and
 * their defaults; the editor's choice is kept in the browser, per screen.
 */
export class ReviewColumns {
	readonly declared: readonly ColumnDeclaration[];
	readonly #storageKey: string;
	#open = $state<ReadonlySet<ReviewColumn>>(new Set());

	constructor(screen: string, declared: readonly ColumnDeclaration[]) {
		this.declared = inShownOrder(declared);
		this.#storageKey = `papyria.columns.${screen}`;
		this.#open = defaultOpen(declared);
	}

	get armsShown(): boolean {
		return armsShown(this.#open);
	}

	isOpen(column: ReviewColumn): boolean {
		return this.#open.has(column);
	}

	canToggle(column: ReviewColumn): boolean {
		return canToggle(this.#open, column);
	}

	toggle(column: ReviewColumn): void {
		if (!this.canToggle(column)) return;
		this.#remember(
			this.isOpen(column)
				? new Set([...this.#open].filter((other) => other !== column))
				: new Set([...this.#open, column]),
		);
	}

	show(column: ReviewColumn): void {
		if (this.isOpen(column)) return;
		this.#remember(new Set([...this.#open, column]));
	}

	/** Called on mount, never during render, so server and client agree. */
	restore(): void {
		const declaredColumns = this.declared.map(({ column }) => column);
		const stored = localStorage.getItem(this.#storageKey);
		this.#open = parseOpenColumns(stored, declaredColumns) ?? this.#open;
	}

	#remember(open: ReadonlySet<ReviewColumn>): void {
		this.#open = open;
		localStorage.setItem(this.#storageKey, serialiseOpenColumns(open));
	}
}

function inShownOrder(
	declared: readonly ColumnDeclaration[],
): readonly ColumnDeclaration[] {
	return REVIEW_COLUMN_ORDER.flatMap((column) =>
		declared.filter((declaration) => declaration.column === column),
	);
}

function defaultOpen(
	declared: readonly ColumnDeclaration[],
): ReadonlySet<ReviewColumn> {
	return new Set(
		declared
			.filter(({ openByDefault }) => openByDefault)
			.map(({ column }) => column),
	);
}
